import styled from '@emotion/styled';
import { Link } from 'react-router-dom';

import { forMobile, hoverable } from '@/design-system';
import { Typography } from '@/shared/design-system';

const HEX_PATTERN = `url("data:image/svg+xml,${encodeURIComponent(
  '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20"><rect x="8" y="8" width="4" height="4" fill="white" fill-opacity="0.02"/></svg>',
)}")`;

export const Wrapper = styled('div')(({ theme }) => ({
  width: '100%',
  minHeight: '100dvh',
  display: 'flex',
  justifyContent: 'center',
  gap: theme.spacing[8],
  boxSizing: 'border-box',
  padding: theme.spacing[8],
  backgroundColor: theme.colors.background.canvas,
  backgroundImage: HEX_PATTERN,

  [forMobile]: {
    flexDirection: 'column',
    gap: 0,
    padding: 0,
  },
}));

export const SkipLink = styled('a')(({ theme }) => ({
  position: 'fixed',
  top: theme.spacing[8],
  left: theme.spacing[8],
  zIndex: 2,
  padding: `${theme.spacing[8]}px ${theme.spacing[12]}px`,
  borderRadius: theme.radius[8],
  backgroundColor: theme.colors.background.raised,
  color: theme.colors.text.primary,
  transform: 'translateY(calc(-100% - 16px))',

  '&:focus-visible': {
    outline: `2px solid ${theme.colors.border.focus}`,
    outlineOffset: 2,
    transform: 'translateY(0)',
  },
}));

export const MobileHeader = styled('header')(({ theme }) => ({
  display: 'none',

  [forMobile]: {
    display: 'flex',
    alignItems: 'center',
    padding: `${theme.spacing[12]}px ${theme.spacing[16]}px`,
    borderBottom: `1px solid ${theme.colors.border.subtle}`,
  },
}));

export const HeaderLogoLink = styled(Link)(({ theme }) => ({
  ...hoverable(theme, 'lift'),
  display: 'inline-flex',
  color: 'inherit',
  borderRadius: theme.radius[8],

  '& svg': { width: 32, height: 32 },

  '&:focus-visible': {
    outline: `2px solid ${theme.colors.border.focus}`,
    outlineOffset: 2,
  },
}));

export const MainWrapper = styled('main')(({ theme }) => ({
  flex: '600 1 0',
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  boxSizing: 'border-box',
  padding: `${theme.spacing[80]}px ${theme.spacing[40]}px`,

  [forMobile]: {
    justifyContent: 'flex-start',
    padding: `${theme.spacing[16]}px ${theme.spacing[16]}px ${theme.spacing[24]}px`,
  },
}));

export const PromoPanel = styled('aside')(({ theme }) => ({
  position: 'sticky',
  top: theme.spacing[8],
  alignSelf: 'flex-start',
  isolation: 'isolate',
  flex: '816 1 0',
  minHeight: `calc(100dvh - ${theme.spacing[16]}px)`,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  boxSizing: 'border-box',
  padding: theme.spacing[40],
  overflow: 'hidden',
  borderRadius: theme.radius[24],
  backgroundColor: theme.colors.black[100],

  [forMobile]: {
    display: 'none',
  },
}));

// Auto size with max bounds keeps the img box equal to the contained photo, so the mask feathers the photo's own edges into the panel.
export const PromoImage = styled('img')({
  position: 'absolute',
  inset: 0,
  zIndex: -2,
  maxWidth: '100%',
  maxHeight: '100%',
  margin: '0 auto auto',
  objectFit: 'contain',
  pointerEvents: 'none',
  maskImage:
    'linear-gradient(to right, transparent, black 12%, black 88%, transparent), linear-gradient(to bottom, black 80%, transparent)',
  maskComposite: 'intersect',
});

export const PromoLight = styled('svg')(({ theme }) => ({
  position: 'absolute',
  inset: 0,
  zIndex: -1,
  width: '100%',
  height: '100%',
  color: theme.colors.brand.solid,
  pointerEvents: 'none',
}));

export const PromoLogo = styled(HeaderLogoLink)({
  alignSelf: 'flex-start',

  '& svg': { width: 162, height: 40 },
});

export const PromoFooter = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'space-between',
  gap: theme.spacing[24],
}));

export const PromoCopy = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[24],
}));

export const PromoTitle = styled(Typography)(({ theme }) => ({
  color: theme.colors.text.primary,

  '& > span': {
    display: 'block',
    color: theme.colors.brand.text,
  },
}));

export const StoreBadges = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing[16],
}));

export const QrBlock = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing[8],
  maxWidth: 176,
  color: theme.colors.text.secondary,
  textAlign: 'center',
}));

export const QrTile = styled('div')(({ theme }) => ({
  width: 152,
  height: 152,
  boxSizing: 'border-box',
  padding: theme.spacing[12],
  borderRadius: theme.radius[12],
  backgroundColor: theme.colors.background.raised,
  color: theme.colors.text.primary,

  '& svg': { width: '100%', height: '100%' },
}));
